import React, { useState, useRef, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Card, CardBody, Container } from "reactstrap";
import i18next from "i18next";
import "./WebinarSection.css";

/**
 * Extract the 11-character YouTube video ID from the common link formats
 * (watch?v=, youtu.be/, /live/, /embed/, /shorts/, /v/). Extra query params
 * such as &t=120s or ?si=... are ignored. Returns null if not a valid link.
 */
export const extractYouTubeId = (url) => {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (trimmed === "") return null;
  const idPattern = /^[a-zA-Z0-9_-]{11}$/;
  try {
    const parsed = new URL(trimmed);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "");
    if (host === "youtu.be") {
      const id = parsed.pathname.split("/").filter(Boolean)[0] || "";
      return idPattern.test(id) ? id : null;
    }
    if (
      host === "youtube.com" ||
      host === "youtube-nocookie.com" ||
      host === "m.youtube.com"
    ) {
      const vParam = parsed.searchParams.get("v");
      if (vParam && idPattern.test(vParam)) return vParam;
      const segments = parsed.pathname.split("/").filter(Boolean);
      if (
        segments.length >= 2 &&
        ["live", "embed", "v", "shorts"].includes(segments[0])
      ) {
        return idPattern.test(segments[1]) ? segments[1] : null;
      }
    }
    return null;
  } catch (e) {
    return null;
  }
};

// Load the YouTube IFrame API script once and resolve when it's ready.
let ytApiPromise = null;
const loadYouTubeApi = () => {
  if (window.YT && window.YT.Player) {
    return Promise.resolve(window.YT);
  }
  if (ytApiPromise) {
    return ytApiPromise;
  }
  ytApiPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") prev();
      resolve(window.YT);
    };
    if (!document.getElementById("youtube-iframe-api")) {
      const tag = document.createElement("script");
      tag.id = "youtube-iframe-api";
      tag.src = "https://www.youtube.com/iframe_api";
      document.body.appendChild(tag);
    }
  });
  return ytApiPromise;
};

/**
 * Webinar section for the project detail page.
 *
 * Renders a privacy-enhanced (youtube-nocookie.com) embedded player at a
 * 16:9 aspect ratio, full width of the content container. The player is only
 * created after the user clicks the poster (lazy load, no autoplay / no
 * cookies until play), and starts playing on that single click via the
 * YouTube IFrame Player API.
 *
 * The 16:9 height is enforced inline in JS (height = width * 9 / 16) so it
 * cannot be broken by the iframe's own width/height attributes or any global
 * stylesheet. If the project has no valid video, nothing is rendered.
 */
const WebinarSection = ({ videoUrl, projectName }) => {
  const [play, setPlay] = useState(false);
  const boxRef = useRef(null);
  const mountRef = useRef(null);
  const playerRef = useRef(null);

  const videoId = extractYouTubeId(videoUrl);

  const title = `${i18next.t("Property_Detail PG Webinar title")}${
    projectName ? ` – ${projectName}` : ""
  }`;

  // Keep the box locked to a 16:9 height based on its own current width.
  const applyAspectRatio = useCallback(() => {
    const box = boxRef.current;
    if (!box) return;
    const width = box.clientWidth;
    if (!width) return;
    const height = Math.round((width * 9) / 16);
    box.style.height = `${height}px`;
    const iframe = box.querySelector("iframe");
    if (iframe) {
      iframe.style.width = "100%";
      iframe.style.height = "100%";
    }
  }, []);

  // Observe the box's own size so the 16:9 height always matches the real
  // rendered width (not the width at an early, pre-layout render).
  useEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;

    applyAspectRatio();

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(() => applyAspectRatio());
      observer.observe(box);
    } else {
      window.addEventListener("resize", applyAspectRatio);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      } else {
        window.removeEventListener("resize", applyAspectRatio);
      }
    };
  }, [applyAspectRatio, play, videoId]);

  useEffect(() => {
    if (!play || !videoId) return undefined;

    let cancelled = false;
    loadYouTubeApi().then((YT) => {
      if (cancelled || !mountRef.current) return;
      playerRef.current = new YT.Player(mountRef.current, {
        videoId,
        host: "https://www.youtube-nocookie.com",
        width: "100%",
        height: "100%",
        playerVars: {
          rel: 0,
          autoplay: 1,
          playsinline: 1,
        },
        events: {
          onReady: (event) => {
            const iframe = event.target.getIframe();
            if (iframe) {
              iframe.setAttribute("title", title);
              iframe.style.position = "absolute";
              iframe.style.top = "0";
              iframe.style.left = "0";
              iframe.style.width = "100%";
              iframe.style.height = "100%";
            }
            applyAspectRatio();
            event.target.playVideo();
          },
        },
      });
    });

    return () => {
      cancelled = true;
      if (playerRef.current && playerRef.current.destroy) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
    };
  }, [play, videoId, title, applyAspectRatio]);

  if (!videoId) {
    return null;
  }

  const thumbnail = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <Container fluid>
      <div className="contentt fullWidth">
        <Card>
          <CardBody>
            <div className="propert-short-des">
              <Card>
                <CardBody>
                  <h5 className="property-inner-title">
                    {i18next.t("Property_Detail PG Webinar title")}
                  </h5>
                  <div className="webinar-player" ref={boxRef}>
                    {play ? (
                      <div className="webinar-player__frame" ref={mountRef} />
                    ) : (
                      <button
                        type="button"
                        className="webinar-player__poster"
                        onClick={() => setPlay(true)}
                        aria-label={title}
                        style={{ backgroundImage: `url(${thumbnail})` }}
                      >
                        <span className="webinar-player__play" aria-hidden="true">
                          <svg viewBox="0 0 68 48" width="68" height="48">
                            <path
                              className="webinar-player__play-bg"
                              d="M66.52 7.74c-.78-2.93-2.49-5.41-5.42-6.19C55.79.13 34 0 34 0S12.21.13 6.9 1.55c-2.93.78-4.63 3.26-5.42 6.19C.06 13.05 0 24 0 24s.06 10.95 1.48 16.26c.78 2.93 2.49 5.41 5.42 6.19C12.21 47.87 34 48 34 48s21.79-.13 27.1-1.55c2.93-.78 4.64-3.26 5.42-6.19C67.94 34.95 68 24 68 24s-.06-10.95-1.48-16.26z"
                            />
                            <path d="M 45,24 27,14 27,34" fill="#fff" />
                          </svg>
                        </span>
                      </button>
                    )}
                  </div>
                </CardBody>
              </Card>
            </div>
          </CardBody>
        </Card>
      </div>
    </Container>
  );
};

WebinarSection.propTypes = {
  videoUrl: PropTypes.string,
  projectName: PropTypes.string,
};

export default WebinarSection;
